import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  approveTrustedNode,
  deleteClusterNode,
  getClusterNodes,
  getClusterStatus,
  revokeTrustedNode,
  triggerBackup,
} from './api'
import type { ClusterNode, ClusterStatus } from './types'

export function ClusterManagement() {
  const { t } = useTranslation()
  const [status, setStatus] = useState<ClusterStatus | null>(null)
  const [nodes, setNodes] = useState<ClusterNode[]>([])
  const [loading, setLoading] = useState(true)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [statusData, nodesData] = await Promise.all([
        getClusterStatus(),
        getClusterNodes(false),
      ])
      setStatus(statusData)
      setNodes(nodesData.nodes || [])
    } catch {
      // Error toast handled by interceptor
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleApprove = async (nodeId: string) => {
    try {
      await approveTrustedNode(nodeId)
      toast.success(t('Node promoted to trusted'))
      fetchData()
    } catch {
      // Error toast handled by interceptor
    }
  }

  const handleRevoke = async (nodeId: string) => {
    try {
      await revokeTrustedNode(nodeId)
      toast.success(t('Trusted status revoked'))
      fetchData()
    } catch {
      // Error toast handled by interceptor
    }
  }

  const handleDelete = async (nodeId: string) => {
    try {
      await deleteClusterNode(nodeId)
      toast.success(t('Node deleted'))
      setDeleteTarget(null)
      fetchData()
    } catch {
      // Error toast handled by interceptor
    }
  }

  const handleBackup = async () => {
    try {
      await triggerBackup()
      toast.success(t('Backup triggered'))
    } catch {
      // Error toast handled by interceptor
    }
  }

  const getRoleBadge = (node: ClusterNode) => {
    if (node.role === 'trusted') {
      return <Badge variant='default'>{t('Trusted')}</Badge>
    }
    if (node.role === 'worker') {
      return <Badge variant='secondary'>{t('Worker')}</Badge>
    }
    return <Badge variant='outline'>{node.role}</Badge>
  }

  const getStatusBadge = (node: ClusterNode) => {
    switch (node.status) {
      case 'online':
        return (
          <Badge variant='default' className='bg-green-600'>
            {t('Online')}
          </Badge>
        )
      case 'offline':
        return <Badge variant='secondary'>{t('Offline')}</Badge>
      case 'joining':
        return <Badge variant='outline'>{t('Joining')}</Badge>
      default:
        return <Badge variant='outline'>{node.status}</Badge>
    }
  }

  const getClusterStateBadge = (node: ClusterNode) => {
    if (node.cluster_state === 'failover') {
      return (
        <Badge variant='default' className='bg-amber-600'>
          {t('Failover')}
        </Badge>
      )
    }
    if (node.cluster_state === 'normal') {
      return (
        <Badge variant='default' className='bg-blue-600'>
          {t('Normal')}
        </Badge>
      )
    }
    return null
  }

  if (!loading && status && !status.enabled) {
    return (
      <div className='flex h-full w-full flex-1 flex-col'>
        <div className='mb-6'>
          <h2 className='text-2xl font-bold'>{t('Cluster Management')}</h2>
          <p className='text-muted-foreground mt-1 text-sm'>
            {t('Manage cluster nodes and trusted node settings')}
          </p>
        </div>
        <Card className='p-8 text-center'>
          <p className='text-muted-foreground text-lg'>
            {t('Cluster is not enabled')}
          </p>
          <p className='text-muted-foreground mt-2 text-sm'>
            {t(
              'Set CLUSTER_ENABLED=true and CLUSTER_SHARED_SECRET environment variables to enable clustering'
            )}
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div className='flex h-full w-full flex-1 flex-col'>
      {/* Header */}
      <div className='mb-6 flex items-center justify-between'>
        <div>
          <h2 className='text-2xl font-bold'>{t('Cluster Management')}</h2>
          <p className='text-muted-foreground mt-1 text-sm'>
            {t('Manage cluster nodes and trusted node settings')}
          </p>
        </div>
        <div className='flex gap-2'>
          <Button variant='outline' onClick={handleBackup}>
            {t('Trigger Backup')}
          </Button>
          <Button variant='outline' onClick={fetchData}>
            {t('Refresh')}
          </Button>
        </div>
      </div>

      {/* Status Cards */}
      <div className='mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
        <Card className='p-4'>
          <p className='text-muted-foreground text-xs font-medium uppercase'>
            {t('Cluster Status')}
          </p>
          <p className='mt-1 text-2xl font-bold'>
            {status?.enabled ? t('Enabled') : t('Disabled')}
          </p>
        </Card>
        <Card className='p-4'>
          <p className='text-muted-foreground text-xs font-medium uppercase'>
            {t('Node Role')}
          </p>
          <p className='mt-1 text-2xl font-bold'>
            {status?.is_master ? t('Master') : t('Worker')}
          </p>
        </Card>
        <Card className='p-4'>
          <p className='text-muted-foreground text-xs font-medium uppercase'>
            {t('Total Nodes')}
          </p>
          <p className='mt-1 text-2xl font-bold'>{status?.node_count ?? 0}</p>
        </Card>
        <Card className='p-4'>
          <p className='text-muted-foreground text-xs font-medium uppercase'>
            {t('Version')}
          </p>
          <p className='mt-1 text-2xl font-bold'>
            {status?.cluster_version ?? '-'}
          </p>
        </Card>
      </div>

      {/* Node Table */}
      <Card>
        <div className='p-4'>
          <h3 className='text-lg font-semibold'>{t('Cluster Nodes')}</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('Node ID')}</TableHead>
              <TableHead>{t('Address')}</TableHead>
              <TableHead>{t('Role')}</TableHead>
              <TableHead>{t('Status')}</TableHead>
              <TableHead>{t('State')}</TableHead>
              <TableHead>{t('CPU')}</TableHead>
              <TableHead>{t('Memory')}</TableHead>
              <TableHead>{t('Backup')}</TableHead>
              <TableHead className='text-right'>{t('Actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className='py-8 text-center'>
                  {t('Loading...')}
                </TableCell>
              </TableRow>
            ) : nodes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className='py-8 text-center'>
                  {t('No nodes registered')}
                </TableCell>
              </TableRow>
            ) : (
              nodes.map((node) => (
                <TableRow key={node.id}>
                  <TableCell className='font-mono text-xs'>{node.id}</TableCell>
                  <TableCell>
                    {node.address}:{node.port}
                  </TableCell>
                  <TableCell>{getRoleBadge(node)}</TableCell>
                  <TableCell>{getStatusBadge(node)}</TableCell>
                  <TableCell>{getClusterStateBadge(node)}</TableCell>
                  <TableCell>{node.cpu.toFixed(0)}%</TableCell>
                  <TableCell>{node.memory.toFixed(0)}%</TableCell>
                  <TableCell>
                    {node.backup_ready ? (
                      <Badge variant='default' className='bg-green-600'>
                        {t('Ready')}
                      </Badge>
                    ) : (
                      <Badge variant='secondary'>{t('N/A')}</Badge>
                    )}
                  </TableCell>
                  <TableCell className='text-right'>
                    <div className='flex justify-end gap-1'>
                      {node.role !== 'trusted' &&
                        node.status === 'online' && (
                          <Button
                            variant='outline'
                            size='sm'
                            onClick={() => handleApprove(node.id)}
                          >
                            {t('Trust')}
                          </Button>
                        )}
                      {node.role === 'trusted' && (
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() => handleRevoke(node.id)}
                        >
                          {t('Untrust')}
                        </Button>
                      )}
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant='outline'
                            size='sm'
                            className='text-destructive'
                            onClick={() => setDeleteTarget(node.id)}
                          >
                            {t('Delete')}
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>{t('Delete Node')}</DialogTitle>
                            <DialogDescription>
                              {t(
                                'Are you sure you want to delete this node? This action cannot be undone.'
                              )}
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter>
                            <DialogClose asChild>
                              <Button variant='outline'>
                                {t('Cancel')}
                              </Button>
                            </DialogClose>
                            <Button
                              variant='destructive'
                              onClick={() => handleDelete(node.id)}
                            >
                              {t('Delete')}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Join Command */}
      <Card className='mt-4'>
        <div className='p-4'>
          <h3 className='mb-2 text-lg font-semibold'>
            {t('Worker Join Command')}
          </h3>
          <p className='text-muted-foreground mb-3 text-sm'>
            {t(
              'Run this command on worker nodes to join them to the cluster:'
            )}
          </p>
          <pre className='bg-muted overflow-x-auto rounded-lg p-4 text-sm'>
            {`set CLUSTER_ENABLED=true && set CLUSTER_MASTER_ADDRESS=your-server-address:1583 && set CLUSTER_SHARED_SECRET=your-secret && set CLUSTER_NODE_ID=node-$(hostname) && new-api.exe --port 1583`}
          </pre>
        </div>
      </Card>
    </div>
  )
}
